/**
 * Редактор скалярного значения по field_type шаблона (KLS FIELD_TYPES).
 * Режим «плоского» значения — как в API карточки РД; для ОИ обёртка в DynamicField мапит CardValuePayload.
 */

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { toDateInputString } from "./formatScalarDisplay";

export type CardScalarFieldType =
  | "string"
  | "text"
  | "number"
  | "date"
  | "boolean"
  | "json"
  | string;

export interface CardScalarControlProps {
  fieldType: CardScalarFieldType;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  id?: string;
  /** ОИ: Switch + подпись; РД: часто Checkbox */
  booleanVariant?: "switch" | "checkbox";
  validationRules?: Record<string, unknown>;
  rowsText?: number;
  rowsJson?: number;
}

export function CardScalarControl({
  fieldType,
  value,
  onChange,
  disabled,
  id,
  booleanVariant = "switch",
  validationRules,
  rowsText = 4,
  rowsJson = 5,
}: CardScalarControlProps) {
  switch (fieldType) {
    case "boolean":
      if (booleanVariant === "checkbox") {
        return (
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id={id}
              checked={Boolean(value)}
              onCheckedChange={(c) => onChange(c === true)}
              disabled={disabled}
            />
          </div>
        );
      }
      return (
        <div className="flex items-center gap-2 pt-1">
          <Switch
            id={id}
            checked={Boolean(value)}
            onCheckedChange={(checked) => onChange(checked)}
            disabled={disabled}
          />
          <Label htmlFor={id} className="text-sm text-muted-foreground">
            {value ? "Да" : "Нет"}
          </Label>
        </div>
      );

    case "number":
      return (
        <Input
          id={id}
          type="number"
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === "" ? null : Number(raw));
          }}
          disabled={disabled}
          min={validationRules?.min != null ? Number(validationRules.min) : undefined}
          max={validationRules?.max != null ? Number(validationRules.max) : undefined}
        />
      );

    case "date":
      return (
        <Input
          id={id}
          type="date"
          value={toDateInputString(value)}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />
      );

    case "text":
      return (
        <Textarea
          id={id}
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={rowsText}
        />
      );

    case "json":
      return (
        <Textarea
          id={id}
          value={
            typeof value === "string"
              ? value
              : value != null
                ? JSON.stringify(value, null, 2)
                : ""
          }
          onChange={(e) => {
            const t = e.target.value.trim();
            if (!t) {
              onChange(null);
              return;
            }
            try {
              onChange(JSON.parse(t));
            } catch {
              onChange(e.target.value);
            }
          }}
          disabled={disabled}
          rows={rowsJson}
          className="font-mono text-xs"
        />
      );

    case "string":
      return (
        <Input
          id={id}
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          maxLength={
            validationRules?.max_length != null ? Number(validationRules.max_length) : undefined
          }
        />
      );

    default:
      return (
        <Input
          id={id}
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
  }
}
